import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ChartPoint, ChartRange, DashboardStats, User } from '@wml-insta/shared';
import { CHART_RANGES } from '@wml-insta/shared';
import { api } from '../api/client.js';
import { loadPersisted, savePersisted } from '../lib/persist.js';
import { SnapshotChart } from '../charts/SnapshotChart.js';
import { FIELD_LABELS, formatDateTime, formatNum } from '../lib/format.js';
import { UpdatedNote } from '../ui/UpdatedNote.js';

function validRange(v: string, fallback: ChartRange = '7d'): ChartRange {
  return (CHART_RANGES as string[]).includes(v) ? (v as ChartRange) : fallback;
}

const RANGES: { key: ChartRange; label: string }[] = [
  { key: '7d', label: '7 дней' },
  { key: '1m', label: 'Месяц' },
  { key: '3m', label: '3 месяца' },
  { key: 'all', label: 'Всё время' },
];

export function DashboardPage({ me }: { me: User }) {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [chart, setChart] = useState<ChartPoint[]>([]);
  // Последний выбранный таймфрейм запоминается на юзера.
  const [range, setRangeRaw] = useState<ChartRange>(() => validRange(loadPersisted<string>(me.id, 'chart.dash.range', '7d')));
  const setRange = (r: ChartRange) => { setRangeRaw(r); savePersisted(me.id, 'chart.dash.range', r); };

  useEffect(() => {
    api<DashboardStats>('/api/stats/dashboard').then(setStats).catch(() => setStats(null));
  }, []);

  useEffect(() => {
    api<ChartPoint[]>(`/api/stats/chart?range=${range}`).then(setChart).catch(() => setChart([]));
  }, [range]);

  if (!stats) return <div className="empty-state">Загрузка…</div>;

  const inWork = stats.byStatus['В работе'] ?? 0;
  const warming = stats.byStatus['Прогрев'] ?? 0;
  const human = stats.byStatus['Хьюман'] ?? 0;
  const banned = stats.byStatus['Бан'] ?? 0;

  return (
    <div className="home-inner" style={{ maxWidth: 1400 }}>
      <div className="hero" style={{ paddingBottom: 24 }}>
        <div className="hero-eyebrow">
          <span className="hero-eyebrow-dot" />
          Ферма Instagram · WML
        </div>
        <h1>
          Вся ферма<br />в одном <span className="accent">чистом</span> виде
        </h1>
        <p>
          {formatNum(stats.totalAccounts)} акков на {stats.phoneCount} телефонах ·{' '}
          {stats.modelCount} моделей. Смотри в разрезе <Link to="/phones" style={{ color: 'var(--accent)' }}>телефонов</Link> или{' '}
          <Link to="/models" style={{ color: 'var(--accent)' }}>моделей</Link>.
        </p>
      </div>

      <div className="stats-row" style={{ gridTemplateColumns: 'repeat(7, 1fr)', margin: '0 0 24px' }}>
        <div className="stat-card">
          <div className="stat-label">Всего акков</div>
          <div className="stat-value">{formatNum(stats.totalAccounts)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">В работе</div>
          <div className="stat-value" style={{ color: '#4ade80' }}>{formatNum(inWork)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">На прогреве</div>
          <div className="stat-value" style={{ color: '#eab308' }}>{formatNum(warming)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Хьюман</div>
          <div className="stat-value" style={{ color: '#60a5fa' }}>{formatNum(human)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Банов всего</div>
          <div className="stat-value stat-accent">{formatNum(banned)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Банов сегодня</div>
          <div className="stat-value stat-accent">{formatNum(stats.bansToday)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Просмотры 24ч</div>
          <div className="stat-value">{formatNum(stats.views24h)}</div>
          <UpdatedNote at={stats.lastParsedAt} variant="block" />
        </div>
      </div>

      <div className="card card-large">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 12 }}>
          <div className="section-title" style={{ margin: 0 }}>Динамика фермы</div>
          <div className="range-tabs range-tabs-compact">
            {RANGES.map((r) => (
              <button key={r.key} className={range === r.key ? 'active' : ''} onClick={() => setRange(r.key)}>
                {r.label}
              </button>
            ))}
          </div>
        </div>
        <SnapshotChart points={chart} height={340} />
      </div>

      {stats.recentEvents.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="section-title">Последние события</div>
          <div className="event-feed">
            {stats.recentEvents.map((e) => (
              <div key={e.id} className="event-row">
                <span className="ev-time">{formatDateTime(e.createdAt)}</span>
                <span className="ev-text">
                  {e.userName && <span className="ev-user">{e.userName} </span>}
                  <b>@{e.username}</b>{' '}
                  {e.field === 'reel_deleted'
                    ? <>рилс <span style={{ color: 'var(--accent)' }}>удалён инстой</span>: {e.newValue} ({e.oldValue})</>
                    : e.field === 'created'
                    ? <>— создан{e.newValue === 'импорт из гугл-таблицы' ? ' (импорт)' : ''}</>
                    : <>— {FIELD_LABELS[e.field] ?? e.field}: <b>{e.oldValue ?? '—'}</b> → <b>{e.newValue ?? '—'}</b></>}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
